import OfferInclusionsList from 'components/Common/Inclusions/OfferInclusionsList'
import Modal from 'LuxKit/Modal/Modal'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import { useAppSelector } from 'hooks/reduxHooks'
import { useDirectSearchPrices } from 'hooks/Search/useSearchPrices'
import useBedbankOfferSearchPricing from 'hooks/useBedbankOfferSearchPricing'
import { EmptyObject } from 'lib/object/objectUtils'
import determineOfferFeatureSymbol from 'lib/offer/determineOfferFeatureSymbol'
import { getHotelOfferUrl } from 'lib/offer/offerPageURL'
import LuxLPPPricingLabel from 'luxPlus/components/LuxLPPPricingLabel'
import { getIsLuxPlusLPPEnabled } from 'luxPlus/selectors/featureToggle'
import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import ExperimentalSearchTileFeaturesList
  from 'components/SearchKit/SearchTiles/shared/ExperimentalSearchTileFeaturesList'
import SearchTileRoomBreakdown from '../shared/SearchTileRoomBreakdown'
import useBedbankRates from 'hooks/useBedbankRates'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import { useActiveAmenityFilters } from 'hooks/useActiveAmenityFilters'
import { reorderAmenitiesByActiveFilters } from 'lib/offer/amenitiesUtils'
import LoyaltyInclusions from 'loyalty/components/LoyaltyInclusions'

interface Props {
  offer: App.BedbankOffer | App.BedbankOfferSummary
  filters?: App.OfferListFilters
}

function ExperimentalBedbankSearchTileFeatures({ offer, filters = EmptyObject }: Props) {
  const intl = useIntl()
  const {
    offerPrice,
    offerMemberPrice,
  } = useBedbankOfferSearchPricing(offer, filters)
  const offerMetaData = useOfferMetaData(offer.id, filters)
  const lowestPriceRoomRateId = offerMetaData?.pricing?.lowestPriceRoomRateId
  const hasInclusionFilters = !!filters.inclusions?.length
  const shouldFetchRates = !!lowestPriceRoomRateId && !!filters.checkIn && !!filters.checkOut && hasInclusionFilters
  const [rates, fetchingRates] = useBedbankRates(
    offer.id,
    filters.rooms ?? [{ adults: 2 }],
    filters.checkIn,
    filters.checkOut,
    shouldFetchRates,
  )

  const rateInclusions = useMemo<Array<App.OfferInclusion> | undefined>(() => {
    if (!lowestPriceRoomRateId || !hasInclusionFilters) {
      return undefined
    }

    const allRates = [...rates.hotelOnlyRates, ...rates.hotelBundleRates]
    const rate = allRates.find(r => r.id === lowestPriceRoomRateId)

    // Return undefined if rate not found (will fall back to popularFacilities)
    // Return empty array if rate found but has no inclusions
    return rate?.inclusions
  }, [lowestPriceRoomRateId, rates, hasInclusionFilters])

  const activeAmenityFilters = useActiveAmenityFilters()
  const amenities = useMemo<Array<App.OfferInclusion>>(() => {
    // Only use rate inclusions if we have inclusion filters selected
    // Otherwise fall back to popularFacilities
    if (hasInclusionFilters && rateInclusions) {
      return rateInclusions
    }

    // Reorder popularFacilities before mapping to boost matching amenities
    const reorderedFacilities = activeAmenityFilters.size > 0
      ? reorderAmenitiesByActiveFilters(offer.popularFacilities, activeAmenityFilters)
      : offer.popularFacilities

    return reorderedFacilities.map(facility => ({
      description: facility,
      id: facility,
      symbol: determineOfferFeatureSymbol(facility),
    }))
  }, [rateInclusions, offer.popularFacilities, hasInclusionFilters, activeAmenityFilters])

  const luxPlusLPPEnabled = useAppSelector(getIsLuxPlusLPPEnabled)
  const directSearchPrices = useDirectSearchPrices({ filters, offerId: offer.id })
  const showModal = useContext(ModalContext)
  const openAllInclusionsModal = useCallback(() => {
    showModal(<Modal
      title={offer.property.name}
      primaryActionTo={getHotelOfferUrl(offer.id, offer.type)}
      primaryActionText={intl.formatMessage({
        id: 'wetNSQuFlvaIbNR2MKlW',
        defaultMessage: 'View offer',
        description: 'CTA button in experimental bedbank inclusions modal to view the offer',
      })}
    >
      <Group direction="vertical" gap={4}>
        <OfferInclusionsList inclusions={amenities} />
        <LoyaltyInclusions offer={offer} variant="offer-page" />
      </Group>
    </Modal>)
  }, [showModal, offer, amenities, intl])

  const savingAmount = offerMetaData?.pricing?.lowestMemberPrice && offerMetaData?.pricing?.lowestPrice ?
    offerMetaData?.pricing?.lowestPrice - offerMetaData?.pricing?.lowestMemberPrice :
    undefined

  const featuresContent = shouldFetchRates && fetchingRates ? (
    <VerticalSpacer gap={4}>
      <TextLoadingBox typography="body-small" width="60%" />
      <TextLoadingBox typography="body-small" width="80%" />
      <TextLoadingBox typography="body-small" width="40%" />
    </VerticalSpacer>
  ) : (
    <>
      <Group direction="vertical" gap={4}>
        <ExperimentalSearchTileFeaturesList
          features={amenities}
          featuresType="amenities"
          onMoreClick={openAllInclusionsModal}
          singleColumn={!!rateInclusions}
        />
        <LoyaltyInclusions offer={offer} variant="search-tile" />
      </Group>
      {luxPlusLPPEnabled && !!offerMetaData?.pricing?.lowestMemberPrice && !!savingAmount && savingAmount > 0 && <LuxLPPPricingLabel
        offer={offer}
        hasLuxPlusRate={directSearchPrices?.hasLuxPlusRate}
        offerMemberPrice={offerMemberPrice}
        offerPrice={offerPrice}
        savingAmount={savingAmount}
      />}
    </>
  )

  return <Group direction="vertical" verticalAlign="space-between" grow gap={8} noDisplayWhenEmpty>
    <Group direction="vertical" gap={8} noDisplayWhenEmpty>
      {featuresContent}
    </Group>
    <SearchTileRoomBreakdown
      offer={offer}
      filters={filters}
    />
  </Group>
}

export default ExperimentalBedbankSearchTileFeatures
